import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { CHANGE_ALL_WIRED_FIELDS } from '../pro/changeAll/wiredFields';
import { TanTable } from '../../components/TanTable';
import { ColumnDef } from '@tanstack/react-table';
import api from '../../services/api';
import { CheckIcon, TrashIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import Notification, { NotificationContainer } from '../../components/Notification';
import { Tooltip } from '../../components/ui/Tooltip';
import { Drawer } from '../../components/ui/Drawer';
import { useDispatchFooter } from '../../contexts/DispatchFooterContext';
import { useFieldDefinitions } from '../../contexts/FieldDefinitionsContext';
import { TextField, SelectField, TextAreaField, Toggle, BoolPill } from '../../components/tablemaint/fields';
import EditHistoryLink from '../../components/tablemaint/EditHistoryLink';

interface FieldDefinition {
  id: number;
  tablename: string;
  scope: string;
  fieldname: string;
  displayname: string;
  abbreviation: string | null;
  type: string;
  join_key: string | null;
  source_alias: string | null;
  source_fieldname: string | null;
  is_displayable: boolean;
  is_filterable: boolean;
  is_mass_editable: boolean;
  /** Whether the flag is actually in effect. False on a flagged field means the code
   *  refuses it for a reason the flag cannot override. null = could not be determined. */
  mass_edit_active?: boolean | null;
  /** Whether the flag reaches the TEMPLATES board too. One flag serves both grids, so
   *  a field can be doing something on one and nothing on the other. */
  mass_edit_templates?: boolean | null;
  /** The camelCase key the /pro order form addresses this field by. The registry is
   *  keyed by DB column and for many order fields the two are unrelated
   *  (`serviceFailure` is stored in `dvirframe`), so Change All cannot name a form
   *  field without this. null = not on the order form. */
  view_key?: string | null;
  /** Whether /pro offers this field an "apply to all pros" checkbox in Change All.
   *  Its own flag, not is_mass_editable: that one answers the same question for the
   *  TanTable grid, and the two sets are deliberately different sizes. */
  is_change_all?: boolean;
  /** Whether THAT flag is in effect. False on a flagged field means the code refuses
   *  it whatever the flag says — an auto-stamped approval column, or no view_key to
   *  name it by. null = could not be determined. */
  change_all_active?: boolean | null;
  /** This column is an auto-stamped approval / availability field the server refuses
   *  to fan whatever the flag says. Independent of is_change_all, so the drawer can
   *  warn while the box is being ticked rather than after the row is saved. */
  change_all_blocked?: boolean;
  enum_source: string | null;
  /** Where an EDIT dropdown gets its options — 'lookup:<key>'. Distinct from
   *  enum_source, which is the FILTER list: on a roster field "what values are in
   *  this column" and "who may be assigned now" are genuinely different lists. */
  edit_source?: string | null;
  sql_expression: string | null;
  aggregate_default: string | null;
  /** Displaying this field can duplicate the pro row (its join is one-to-many).
   *  Computed by the backend from app.core.row_expanding, not a stored column. */
  expands?: boolean;
  created_at?: string | null;
  updated_at?: string | null;
}

interface JoinDefinition {
  id: number;
  join_key: string;
  target_alias: string;
  target_table: string;
  description: string | null;
}

// Filter domains a field definition can belong to (see field_definitions.scope).
const SCOPES = ['orders', 'quotes', 'edi_orders'];
const FIELD_TYPES = ['string', 'number', 'date', 'boolean', 'datetime', 'text', 'decimal'];

// Starting calculation for the TanTable totals bar (the Σ button). Blank means
// the column shows no total until a user picks one; a user's own pick always
// wins over this. Values are the ids the frontend calc engine uses, and the
// options offered are narrowed to the ones that make sense for the field's Type.
const AGGREGATE_DEFAULTS: { value: string; label: string; types: string[] }[] = [
  { value: 'sum', label: 'Sum', types: ['number', 'decimal'] },
  { value: 'avg', label: 'Average', types: ['number', 'decimal'] },
  { value: 'median', label: 'Median', types: ['number', 'decimal'] },
  { value: 'min', label: 'Min', types: ['number', 'decimal'] },
  { value: 'max', label: 'Max', types: ['number', 'decimal'] },
  { value: 'earliest', label: 'Earliest', types: ['date', 'datetime'] },
  { value: 'latest', label: 'Latest', types: ['date', 'datetime'] },
  // Counting works on anything, including booleans and free text.
  { value: 'count', label: 'Count', types: [] },
  { value: 'countDistinct', label: 'Count distinct', types: [] },
];

/** Calc options valid for a field Type; `types: []` means "valid for every type". */
const aggregateOptionsForType = (type: string | undefined) =>
  AGGREGATE_DEFAULTS.filter((o) => o.types.length === 0 || o.types.includes(type || ''))
    .map((o) => ({ value: o.value, label: o.label }));

const EMPTY_FORM: Partial<FieldDefinition> = {
  scope: 'orders', tablename: '', fieldname: '', displayname: '', abbreviation: '',
  type: 'string', join_key: null, source_alias: null, source_fieldname: null,
  is_displayable: true, is_filterable: true,
  // Off by default, unlike its two siblings: displaying and filtering a field are
  // harmless, writing it across a batch of live orders is not.
  is_mass_editable: false,
  // Same reasoning, and the /pro answer rather than the grid's.
  is_change_all: false, view_key: null,
  enum_source: null, edit_source: null, sql_expression: null,
  aggregate_default: null,
};

const FieldManager: React.FC = () => {
  const { setDispatchButton } = useDispatchFooter();
  const { refetch: refetchGlobalFieldDefs } = useFieldDefinitions();
  const [data, setData] = useState<FieldDefinition[]>([]);
  const [joinDefinitions, setJoinDefinitions] = useState<JoinDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorNotification, setErrorNotification] = useState<{ title: string; message: string } | null>(null);
  const [successNotification, setSuccessNotification] = useState<{ title: string; message: string } | null>(null);

  // Drawer (create + edit). editingId === null => create.
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<Partial<FieldDefinition>>(EMPTY_FORM);
  const [isSaving, setIsSaving] = useState(false);
  const [drawerError, setDrawerError] = useState<string | null>(null);
  const [isRebuilding, setIsRebuilding] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [fields, joins] = await Promise.all([
        api.get('/field-definitions', { params: { scope: 'all' } }),
        api.get('/join-definitions'),
      ]);
      setData(fields.data.data || []);
      setJoinDefinitions(joins.data.data || []);
    } catch (error) {
      console.error('Error loading field definitions:', error);
      setErrorNotification({ title: 'Failed to Load', message: 'Could not load field definitions from the server.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const setField = (key: keyof FieldDefinition, value: any) => setForm((prev) => ({ ...prev, [key]: value }));

  /**
   * Why ticking Change All will not do anything, or null when it will.
   *
   * The grid says the same thing per row, but only for rows as they were LOADED — and
   * the moment that matters is while someone is ticking the box, before they save and
   * go looking on /pro for a checkbox that was never going to appear. So this reads the
   * live form, and the two conditions that depend on the View Key re-evaluate as it is
   * typed.
   *
   * Ordered most-fundamental first. A stamp column can never be opened by anyone, so
   * saying "no checkbox on the form" about one would send an admin off to ask for a
   * render site that must not exist.
   */
  const changeAllWarning = useMemo((): { title: string; detail: string } | null => {
    if (!form.is_change_all) return null;
    if (form.change_all_blocked) {
      return {
        title: 'Not supported',
        detail: 'this is an auto-stamped approval or availability column. The server '
          + 'refuses to fan it whatever this flag says, because copying a stamp across '
          + 'a batch forges the record of an act rather than repeating it.',
      };
    }
    if (!form.view_key) {
      return {
        title: 'No View Key',
        detail: 'the order form addresses its fields by camelCase key, so without one '
          + 'there is no field for a checkbox to attach to. Set the View Key above.',
      };
    }
    if (!CHANGE_ALL_WIRED_FIELDS.has(form.view_key)) {
      return {
        title: 'No checkbox on the order form',
        detail: `nothing renders a Change All checkbox for "${form.view_key}", so this `
          + 'flag will save and have no effect. Adding one is a code change — the '
          + "field's render site has to name it.",
      };
    }
    return null;
  }, [form.is_change_all, form.change_all_blocked, form.view_key]);

  // Selecting a join_key auto-fills source_alias from that join definition (still editable).
  const onJoinKeyChange = (joinKey: string) => {
    if (!joinKey) {
      setForm((prev) => ({ ...prev, join_key: null, source_alias: null }));
      return;
    }
    const j = joinDefinitions.find((jd) => jd.join_key === joinKey);
    setForm((prev) => ({ ...prev, join_key: joinKey, source_alias: j ? j.target_alias : prev.source_alias ?? null }));
  };

  const openAdd = useCallback(() => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setDrawerError(null);
    setDrawerOpen(true);
  }, []);

  const openEdit = useCallback((row: FieldDefinition) => {
    setEditingId(row.id);
    setForm({ ...row });
    setDrawerError(null);
    setDrawerOpen(true);
  }, []);

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
    setDrawerError(null);
  };

  const handleSave = useCallback(async () => {
    if (!form.tablename?.trim() || !form.fieldname?.trim() || !form.displayname?.trim() || !form.type?.trim()) {
      setDrawerError('Table name, field name, display name, and type are required.');
      return;
    }
    setIsSaving(true);
    setDrawerError(null);
    try {
      if (editingId != null) {
        await api.put(`/field-definitions/${editingId}`, form);
        setSuccessNotification({ title: 'Updated', message: `Field definition for ${form.tablename}.${form.fieldname} updated.` });
      } else {
        await api.post('/field-definitions', form);
        setSuccessNotification({ title: 'Created', message: `Field definition for ${form.tablename}.${form.fieldname} created.` });
      }
      refetchGlobalFieldDefs();
      closeDrawer();
      await loadData();
    } catch (error: any) {
      console.error('Error saving field definition:', error);
      setDrawerError(error?.response?.data?.detail || error?.message || 'Unknown error occurred');
    } finally {
      setIsSaving(false);
    }
  }, [form, editingId, refetchGlobalFieldDefs, loadData]);

  const handleDelete = useCallback(async (row: FieldDefinition) => {
    if (!window.confirm(`Delete the field definition for ${row.tablename}.${row.fieldname}?`)) return;
    try {
      await api.delete(`/field-definitions/${row.id}`);
      setSuccessNotification({ title: 'Deleted', message: `Field definition for ${row.tablename}.${row.fieldname} deleted.` });
      refetchGlobalFieldDefs();
      await loadData();
    } catch (error: any) {
      console.error('Error deleting field definition:', error);
      setErrorNotification({ title: 'Failed to Delete', message: error?.response?.data?.detail || error?.message || 'Unknown error occurred' });
    }
  }, [refetchGlobalFieldDefs, loadData]);

  const handleSync = useCallback(async () => {
    // Add-only: the backend runs populate_fields_updated.py in its default mode, which
    // inserts missing rows and never writes over one that already exists.
    if (!window.confirm('Sync field definitions from populate_fields_updated.py? Only fields that are missing get added — existing rows are left untouched.')) return;
    setIsRebuilding(true);
    try {
      const res = await api.post('/field-definitions/rebuild');
      const added = res.data?.added_records ?? 0;
      setSuccessNotification({
        title: 'Sync Complete',
        message: added > 0
          ? `${added} new field definition${added === 1 ? '' : 's'} added (${res.data?.total_records ?? ''} total). Existing rows were left unchanged.`
          : `No new field definitions to add (${res.data?.total_records ?? ''} total). Existing rows were left unchanged.`,
      });
      refetchGlobalFieldDefs();
      await loadData();
    } catch (error: any) {
      console.error('Error syncing field definitions:', error);
      setErrorNotification({ title: 'Sync Failed', message: error?.response?.data?.detail || error?.message || 'Unknown error occurred' });
    } finally {
      setIsRebuilding(false);
    }
  }, [refetchGlobalFieldDefs, loadData]);

  // Sync Fields button lives in the shared dispatch footer (page-level action).
  useEffect(() => {
    setDispatchButton(
      <button
        onClick={handleSync}
        disabled={isRebuilding}
        className="px-4 py-2 text-sm font-medium text-accent-fg bg-accent rounded-lg hover:bg-accent-hover disabled:opacity-50 inline-flex items-center gap-2"
      >
        <ArrowPathIcon className={`w-4 h-4 ${isRebuilding ? 'animate-spin' : ''}`} />
        {isRebuilding ? 'Syncing…' : 'Sync Fields'}
      </button>,
    );
    return () => setDispatchButton(null);
  }, [setDispatchButton, handleSync, isRebuilding]);

  const truncCell = (v: any) =>
    v ? <span title={String(v)} className="block max-w-[220px] truncate text-xs">{String(v)}</span> : '';

  const columns = useMemo<ColumnDef<FieldDefinition>[]>(() => [
    { accessorKey: 'scope', header: 'Scope', size: 80, enableColumnFilter: true, filterFn: 'arrIncludes' },
    { accessorKey: 'tablename', header: 'Table', size: 130, enableColumnFilter: true, filterFn: 'arrIncludes' },
    { accessorKey: 'fieldname', header: 'Field', size: 150, enableColumnFilter: true, filterFn: 'arrIncludes' },
    { accessorKey: 'displayname', header: 'Display Name', size: 160 },
    { accessorKey: 'abbreviation', header: 'Abbreviation', size: 110, cell: ({ row }) => row.original.abbreviation || '' },
    { accessorKey: 'type', header: 'Type', size: 90, enableColumnFilter: true, filterFn: 'arrIncludes' },
    { accessorKey: 'join_key', header: 'Join Key', size: 120, enableColumnFilter: true, filterFn: 'arrIncludes', cell: ({ row }) => row.original.join_key || '' },
    { accessorKey: 'source_alias', header: 'Src Alias', size: 90, cell: ({ row }) => row.original.source_alias || '' },
    { accessorKey: 'source_fieldname', header: 'Src Field', size: 150, cell: ({ row }) => row.original.source_fieldname || '' },
    {
      accessorKey: 'aggregate_default', header: 'Aggregate', size: 100,
      enableColumnFilter: true, filterFn: 'arrIncludes',
      cell: ({ row }) => {
        const v = row.original.aggregate_default;
        return v ? (AGGREGATE_DEFAULTS.find((o) => o.value === v)?.label ?? v) : '';
      },
    },
    { accessorKey: 'is_displayable', header: 'Display', size: 80, cell: ({ row }) => <BoolPill value={!!row.original.is_displayable} /> },
    { accessorKey: 'is_filterable', header: 'Filter', size: 80, cell: ({ row }) => <BoolPill value={!!row.original.is_filterable} /> },
    {
      accessorKey: 'is_mass_editable',
      header: 'Mass Edit',
      size: 110,
      // A flag the code refuses is a silent no-op otherwise — the field looks enabled
      // and simply never becomes editable. Say so here rather than leaving someone to
      // flip it, hard-refresh, and find nothing changed.
      cell: ({ row }) => (
        <span className="inline-flex items-center gap-1.5">
          <BoolPill value={!!row.original.is_mass_editable} />
          {row.original.is_mass_editable && row.original.mass_edit_active === false && (
            <span
              className="text-[0.625rem] font-semibold text-amber-700 dark:text-amber-400"
              title="This field is flagged, but the grid cannot offer it — the field type has no editor, or it is a foreign key needing a picker."
            >
              not supported
            </span>
          )}
          {/* One flag, two grids. A field open on Search Results and locked on the
              Templates board is the same silent no-op the marker above exists to
              prevent — someone flags it, opens the Templates grid, and nothing
              happens. Say which surface it reached. */}
          {row.original.is_mass_editable && row.original.mass_edit_active
            && row.original.mass_edit_templates === false && (
            <span
              className="text-[0.625rem] font-semibold text-slate-500 dark:text-slate-400"
              title="Offered on Search Results but not on the Templates board — the template field policy locks this field, or it is a leg / accessorial field that surface does not offer."
            >
              orders only
            </span>
          )}
        </span>
      ),
    },
    {
      accessorKey: 'is_change_all',
      header: 'Change All',
      size: 110,
      // The /pro answer, beside the grid's. Same silent-no-op problem, two extra ways
      // to hit it: the field may be a stamp the server never fans, or it may have no
      // view_key at all, which means the registry knows the column but not the form
      // field that writes it — so nothing can draw a checkbox no matter what.
      cell: ({ row }) => (
        <span className="inline-flex items-center gap-1.5">
          <BoolPill value={!!row.original.is_change_all} />
          {row.original.is_change_all && row.original.change_all_active === false && (
            <span
              className="text-[0.625rem] font-semibold text-amber-700 dark:text-amber-400"
              title={row.original.view_key
                ? 'This field is flagged, but /pro will not fan it out — it is an auto-stamped approval or availability column, which a bulk copy would forge rather than repeat.'
                : 'This field is flagged but has no View Key, so the order form has no field to attach a checkbox to. Set the View Key first.'}
            >
              {row.original.view_key ? 'not supported' : 'no view key'}
            </span>
          )}
          {/* The third way a flag does nothing, and the only one the SERVER cannot
              see: the field passes every check but no component renders a checkbox
              for it, because no render site names it. Resolved here in the browser
              against the order form's own wiring — a Python service reading this
              table has no view of JSX. Reported even when change_all_active is true,
              since as far as the server is concerned nothing is wrong. */}
          {row.original.is_change_all && !!row.original.view_key
            && !CHANGE_ALL_WIRED_FIELDS.has(row.original.view_key) && (
            <span
              className="text-[0.625rem] font-semibold text-amber-700 dark:text-amber-400"
              title="This field is flagged, but no checkbox exists for it on the order form — nothing will appear in Change All. Adding one is a code change: the field's render site has to name it."
            >
              no checkbox on form
            </span>
          )}
        </span>
      ),
    },
    { accessorKey: 'view_key', header: 'View Key', size: 150, cell: ({ row }) => truncCell(row.original.view_key) },
    { accessorKey: 'enum_source', header: 'Enum Source (filter)', size: 160, cell: ({ row }) => truncCell(row.original.enum_source) },
    { accessorKey: 'edit_source', header: 'Edit Source', size: 160, cell: ({ row }) => truncCell(row.original.edit_source) },
    { accessorKey: 'sql_expression', header: 'SQL Expr', size: 200, cell: ({ row }) => truncCell(row.original.sql_expression) },
    { accessorKey: 'expands', header: 'Expands Rows', size: 110, cell: ({ row }) => <BoolPill value={!!row.original.expands} /> },
    { accessorKey: 'id', header: 'ID', size: 70, meta: { align: 'right' } },
    { accessorKey: 'created_at', header: 'Created', size: 140, cell: ({ row }) => row.original.created_at || '' },
    { accessorKey: 'updated_at', header: 'Updated', size: 140, cell: ({ row }) => row.original.updated_at || '' },
    {
      id: 'actions', header: 'Actions', size: 70, enableSorting: false, enableColumnFilter: false,
      // Row click opens the editor; delete stays here (stopPropagation so it
      // doesn't also open the drawer).
      cell: ({ row }) => (
        <div className="flex gap-2 justify-end">
          <Tooltip content="Delete">
            <button onClick={(e) => { e.stopPropagation(); handleDelete(row.original); }} className="p-1 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300">
              <TrashIcon className="w-4 h-4" />
            </button>
          </Tooltip>
        </div>
      ),
    },
  ], [handleDelete]);

  // Every column the /field-definitions payload carries is declared above, so
  // nothing on the base table is unreachable from the grid — no code change to
  // surface the next one somebody asks for. The bulky/administrative ones start
  // hidden and are turned on from Show/Hide Columns; `abbreviation` is the
  // exception and ships visible, since that is the one users asked for.
  //
  // TanTable applies this map to columns ABSENT from a user's saved layout, so
  // existing layouts keep their column set and simply gain these as options.
  const defaultColumnVisibility = useMemo(
    () => ({
      source_fieldname: false,
      expands: false,
      id: false,
      created_at: false,
      updated_at: false,
    }),
    [],
  );

  const joinKeyOptions = useMemo(
    () => [{ value: '', label: '— None —' }, ...joinDefinitions.map((j) => ({ value: j.join_key, label: j.join_key, description: j.description || j.target_table }))],
    [joinDefinitions],
  );

  // The closed set of EDIT option sources, from the server. Closed because the server
  // resolves the same key to validate every written value — a key it cannot resolve is
  // a field that silently renders read-only, so there is nothing to gain from letting
  // an admin type one.
  const [editSources, setEditSources] = useState<{ key: string; label: string }[]>([]);
  useEffect(() => {
    let alive = true;
    api.get('/field-definitions/edit-sources')
      .then((res) => { if (alive) setEditSources(res.data?.sources ?? []); })
      .catch(() => { /* the field keeps whatever it has; the picker just stays empty */ });
    return () => { alive = false; };
  }, []);
  const editSourceOptions = useMemo(
    () => [
      { value: '', label: '— None —' },
      ...editSources.map((s) => ({ value: `lookup:${s.key}`, label: s.label })),
    ],
    [editSources],
  );

  return (
    <div className="h-full w-full p-10 pb-24">
      <TanTable
        data={data}
        columns={columns}
        loading={loading}
        settingsKey="field-manager-table"
        fieldName="field-manager-table"
        defaultColumnVisibility={defaultColumnVisibility}
        searchPlaceholder="Search field definitions…"
        cardTitle="Field Definitions"
        enableGrouping={false}
        enableColumnFilters={true}
        onEditFilters={openAdd}
        showEditFiltersButton={true}
        editFiltersButtonIcon="plus"
        editFiltersButtonTooltip="Add Field Definition"
        onRowClick={openEdit}
        lockedColumns={[{ id: 'actions', side: 'right' }]}
      />

      <Drawer
        isOpen={drawerOpen}
        onClose={closeDrawer}
        title={editingId != null ? 'Edit Field Definition' : 'Add Field Definition'}
        position="right"
        maxWidth="lg"
        footer={
          <div className="flex items-center justify-end gap-3">
            <button onClick={closeDrawer} disabled={isSaving} className="px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50">
              Cancel
            </button>
            <button onClick={handleSave} disabled={isSaving} className="px-4 py-2 text-sm font-medium text-accent-fg bg-accent rounded-lg hover:bg-accent-hover disabled:opacity-50 inline-flex items-center gap-2">
              <CheckIcon className="w-4 h-4" />
              {isSaving ? 'Saving…' : editingId != null ? 'Save Changes' : 'Add Field'}
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          {editingId != null && (
            <div className="flex justify-end">
              <EditHistoryLink endpoint={`/field-definitions/${editingId}/history`} label={`${form.tablename}.${form.fieldname}`} />
            </div>
          )}
          {drawerError && (
            <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">
              {drawerError}
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SelectField label="Scope" required clearable={false} value={form.scope ?? 'orders'} onChange={(v) => setField('scope', v)} options={SCOPES.map((s) => ({ value: s, label: s }))} disabled={isSaving} />
            <SelectField label="Type" required clearable={false} value={form.type ?? 'string'} onChange={(v) => setField('type', v)} options={FIELD_TYPES.map((t) => ({ value: t, label: t }))} disabled={isSaving} />
            <TextField label="Table Name" required value={form.tablename ?? ''} onChange={(v) => setField('tablename', v)} placeholder="e.g., orderscurrent" disabled={isSaving} />
            <TextField label="Field Name" required value={form.fieldname ?? ''} onChange={(v) => setField('fieldname', v)} placeholder="e.g., pronumber" disabled={isSaving} />
            <TextField label="Display Name" required value={form.displayname ?? ''} onChange={(v) => setField('displayname', v)} disabled={isSaving} />
            <TextField label="Abbreviation" value={form.abbreviation ?? ''} onChange={(v) => setField('abbreviation', v)} disabled={isSaving} />
            <SelectField label="Join Key" value={form.join_key ?? ''} onChange={onJoinKeyChange} options={joinKeyOptions} showDescriptions disabled={isSaving} />
            <TextField label="Source Alias" value={form.source_alias ?? ''} onChange={(v) => setField('source_alias', v)} placeholder="Auto-filled from join key" disabled={isSaving} />
            <TextField label="Source Field Name" value={form.source_fieldname ?? ''} onChange={(v) => setField('source_fieldname', v)} disabled={isSaving} />
            <TextField label="Enum Source (filter options)" value={form.enum_source ?? ''} onChange={(v) => setField('enum_source', v || null)} placeholder="table.column or static:val1,val2" disabled={isSaving} />
            {/* The EDIT list, which is a different question from the filter list above:
                enum_source says what values are IN the column (history included, which
                is what a filter wants), this says what may be WRITTEN now. A picker
                rather than free text because the server resolves the same key to
                validate every written value — so it cannot name something uncheckable.
                Blank = no edit dropdown, and a `static:` enum_source still serves as
                one on its own. */}
            <SelectField
              label="Edit Source (edit options)"
              value={form.edit_source ?? ''}
              onChange={(v) => setField('edit_source', v)}
              options={editSourceOptions}
              disabled={isSaving}
            />
            {/* Clearable on purpose: blank = the column shows no total until a user
                picks one. Sends "" rather than null on clear — the PUT reads null as
                "leave unchanged", so null could never clear a value. */}
            <SelectField label="Aggregate Default" value={form.aggregate_default ?? ''} onChange={(v) => setField('aggregate_default', v)} options={aggregateOptionsForType(form.type)} disabled={isSaving} />
            <Toggle label="Displayable" checked={!!form.is_displayable} onChange={(v) => setField('is_displayable', v)} disabled={isSaving} />
            <Toggle label="Filterable" checked={!!form.is_filterable} onChange={(v) => setField('is_filterable', v)} disabled={isSaving} />
            {/* Whether the Search Results grid may offer this field for inline / bulk
                edit. Only ever NARROWS: a field still has to clear the change-all
                allowlist, its widget type, the per-field component permissions, the
                movetype display rules and the cross-field rules. Turning it on for
                something those refuse simply does nothing. */}
            <Toggle label="Mass Editable" checked={!!form.is_mass_editable} onChange={(v) => setField('is_mass_editable', v)} disabled={isSaving} />
            {/* The camelCase key the /pro order form knows this field by. Needed
                because the form addresses fields by view key and this table is keyed
                by DB column, and for many order fields the two are unrelated —
                serviceFailure is stored in dvirframe, idoNumber in freightowner. Blank
                = not on the order form, which is also what makes Change All below
                impossible to turn on usefully. Sends "" rather than null on clear: the
                PUT reads null as "leave unchanged". */}
            <TextField label="View Key (order form)" value={form.view_key ?? ''} onChange={(v) => setField('view_key', v)} placeholder="e.g. sealNumber" disabled={isSaving} />
            {/* Whether /pro offers this field an "apply to all pros" checkbox. Its own
                flag rather than a reuse of Mass Editable above, which answers the same
                question for the TanTable grid — the two sets are deliberately
                different sizes. Only ever NARROWS, like its sibling: the field still
                has to be on the form, have a View Key, clear its component permissions
                and the movetype display rules, and not be an auto-stamped approval
                column, which the server refuses whatever this says. */}
            <Toggle label="Change All (/pro)" checked={!!form.is_change_all} onChange={(v) => setField('is_change_all', v)} disabled={isSaving} />
            {changeAllWarning && (
              <div
                role="status"
                className="rounded border border-amber-300 bg-amber-50 px-2 py-1.5 text-[0.6875rem] leading-snug text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200"
              >
                <span className="font-semibold">{changeAllWarning.title}</span>
                {' — '}
                {changeAllWarning.detail}
              </div>
            )}
            <TextAreaField label="SQL Expression" value={form.sql_expression ?? ''} onChange={(v) => setField('sql_expression', v || null)} rows={3} disabled={isSaving} />
          </div>
        </div>
      </Drawer>

      {errorNotification && createPortal(
        <NotificationContainer position="top-right">
          <Notification type="error" title={errorNotification.title} message={errorNotification.message} showClose onClose={() => setErrorNotification(null)} autoDismiss={5000} />
        </NotificationContainer>,
        document.body,
      )}
      {successNotification && createPortal(
        <NotificationContainer position="top-right">
          <Notification type="success" title={successNotification.title} message={successNotification.message} showClose onClose={() => setSuccessNotification(null)} autoDismiss={3000} />
        </NotificationContainer>,
        document.body,
      )}
    </div>
  );
};

export default FieldManager;
